import { mm, Viewer } from "@inovitas/infra3dsdk";

import ClientConfig from "shared/config";

export const addLayers = (
  viewer: Viewer,
  configurations: any,
  all_sources_loaded_cbf?: (loaded_sources: any[]) => void
) => {
  const loaded_sources: any[] = [];

  if (configurations.length > 0)
    _addSource(
      configurations.shift(),
      loaded_sources,
      configurations,
      viewer,
      all_sources_loaded_cbf
    );
};

function _addSource(
  source: any,
  loaded_sources: any,
  configurations: any,
  viewer: Viewer,
  all_sources_loaded_cbf?: (configurations: any) => void
) {
  function _source_loaded_cbf(loaded_source?: any) {
    if (loaded_source) {
      loaded_sources.push(loaded_source);
    }
    const next_source = configurations.shift();
    if (next_source) {
      _addSource(next_source, loaded_sources, configurations, viewer);
    } else {
      if (all_sources_loaded_cbf) all_sources_loaded_cbf(loaded_sources);
    }
  }

  switch (source.type) {
    case "memory":
      _addLayersFromMemory(
        viewer,
        source,
        _source_loaded_cbf,
        _source_loaded_cbf
      );
      break;
    case "infra3dgeoserver":
      _addLayersFromInfra3DGeoserver(
        viewer,
        source,
        _source_loaded_cbf,
        _source_loaded_cbf
      );
      break;
    case "file":
      _addLayersFromFile(
        viewer,
        source,
        _source_loaded_cbf,
        _source_loaded_cbf
      );
      break;
    case "arcgis":
      _addLayersFromArcGIS(
        viewer,
        source,
        _source_loaded_cbf,
        _source_loaded_cbf
      );
      break;
    case "qgiswfs":
      _addLayersFromQGIS(
        viewer,
        source,
        _source_loaded_cbf,
        _source_loaded_cbf
      );
      break;
    case "wfs20":
      _addLayersFromWFS20(
        viewer,
        source,
        _source_loaded_cbf,
        _source_loaded_cbf
      );
      break;
    case "wfs":
      _addLayersFromWFS(viewer, source, _source_loaded_cbf, _source_loaded_cbf);
      break;
  }
}

function _addLayersFromMemory(
  viewer: Viewer,
  source: any,
  cbs: (source: any) => void,
  cbf?: () => void
) {
  try {
    source.layers.forEach((layerconf: any) => {
      const geojson = layerconf.data || {
        type: "FeatureCollection",
        features: [],
      };
      const options = mm.MemoryLayer.optionsFromData(geojson, {
        data: geojson,
        ...source,
        ...JSON.parse(JSON.stringify(layerconf)),
      });
      const layer = viewer
        .getScene()
        .getLayerHandler()
        .addLayer(mm.LAYERTYPE.MEMORYLAYER, options);
      layerconf.id = layer.id;
    });
    cbs(source);
  } catch (error) {
    if (cbf) cbf();
    console.warn(error);
  }
}

function _addLayersFromInfra3DGeoserver(
  viewer: Viewer,
  source: any,
  cbs: (source: any) => void,
  cbf?: () => void
) {
  source.url = ClientConfig.geofeatureserverURL;
  const api = new mm.Infra3DGeoserver(
    source.url,
    source.project,
    viewer,
    source.source_options // To support the classic configuration
  );
  api.readlayerdefinition(
    (ldefinitions) => {
      const layers_geosrv = ldefinitions.layers;
      const layers_geosrv_map: any = {};
      layers_geosrv?.map((layer_geosrv: any) => {
        layers_geosrv_map[layer_geosrv.layername] = layer_geosrv;
      });

      // select all layers if no or empty list is given in geodataconfiguration
      const layers_gdr = source.layers || [];
      let _loadalllayers = false;
      if (!source.layers) {
        _loadalllayers = true;
        source.layers = layers_gdr;
      } else {
        if (source.layers.length === 0) _loadalllayers = true;
      }
      if (_loadalllayers) {
        layers_geosrv.map((layer_geosrv: any) => {
          layers_gdr.push({
            layername: layer_geosrv.layername,
            topic: source.topic || "",
          });
        });
      }

      for (let i = 0; i < layers_gdr.length; i++) {
        const layer_gdr = layers_gdr[i];
        if (layers_geosrv_map.hasOwnProperty(layer_gdr.layername)) {
          const layer_geosrv = layers_geosrv_map[layer_gdr.layername];

          const layer = viewer
            .getScene()
            .getLayerHandler()
            .addLayer(
              mm.LAYERTYPE.INFRA3DLAYER,
              mm.Infra3DLayer.optionsFromLayerdefinition(layer_geosrv, {
                api: api,
                ...source,
                ...JSON.parse(JSON.stringify(layer_gdr)),
              })
            );
          layer_gdr.id = layer.id;
        } else {
          console.warn("Layer " + layer_gdr.layername + " is not available.");
        }
      }
      cbs(source);
      return {};
    },
    (error) => {
      if (cbf) cbf();
      console.log(error);
      return {};
    }
  );
}

function _addLayersFromFile(
  viewer: Viewer,
  source: any,
  cbs: (source: any) => void,
  cbf?: () => void
) {
  const loaded_layers: any = [];
  const load_layer = (layerconf: any, all_layers_loaded_cbf: any) => {
    const layer_loaded_cbf = (loaded_layer: any) => {
      loaded_layers.push(loaded_layer);
      const next_layer = source.layers.shift();
      if (next_layer) {
        load_layer(next_layer, all_layers_loaded_cbf);
      } else {
        source.layers = loaded_layers;
        if (all_layers_loaded_cbf) all_layers_loaded_cbf(source);
      }
    };

    fetch(`${source.url}/${layerconf.layername}`)
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(
            `Could not load: ${source.url} : ${response.statusText}`
          );
        }
        const geojson = await response.json();
        const options = mm.MemoryLayer.optionsFromData(geojson, {
          data: geojson,
          ...source,
          ...layerconf,
        });
        const layer = viewer
          .getScene()
          .getLayerHandler()
          .addLayer(mm.LAYERTYPE.MEMORYLAYER, options);
        layerconf.id = layer.id;
        layer_loaded_cbf(layerconf);
      })
      .catch((error) => {
        if (cbf) cbf();
        console.log(error.message);
      });
  };
  load_layer(source.layers.shift(), cbs);
}

function _addLayersFromArcGIS(
  viewer: Viewer,
  source: any,
  cbs: (source: any) => void,
  cbf?: () => void
) {
  const arcgis_api = new mm.ArcgisFeatureServer(
    source.url,
    source.project,
    source.source_options
  );
  arcgis_api.readfeatureservice(
    (readfeatureservice_pjson) => {
      let _loadalllayers = false;
      const tclayers: any = {};

      if (!source.layers) {
        _loadalllayers = true;
      } else {
        if (source.layers.length === 0) _loadalllayers = true;
      }

      if (_loadalllayers) {
        readfeatureservice_pjson.layers.map((layer_pjson: any) => {
          tclayers[layer_pjson.id] = { layername: layer_pjson.id };
        });
      } else {
        source.layers.map((tclayer: any) => {
          tclayers[tclayer.layername] = tclayer;
        });
      }

      const layers_to_load = Object.keys(tclayers).map((key) => {
        return tclayers[key];
      });
      const loaded_layers: any = [];
      const load_layer = (
        layer: any,
        all_layers_loaded_cbf: (source: any) => void
      ) => {
        const layer_loaded_cbf = (loaded_layer: any) => {
          loaded_layers.push(loaded_layer);
          const next_layer = layers_to_load.shift();
          if (next_layer) {
            load_layer(next_layer, all_layers_loaded_cbf);
          } else {
            source.layers = loaded_layers;
            if (all_layers_loaded_cbf) all_layers_loaded_cbf(source);
          }
        };
        arcgis_api.readlayer(
          layer.layername,
          (pjson) => {
            const theLayer = viewer
              .getScene()
              .getLayerHandler()
              .addLayer(
                mm.LAYERTYPE.ARCGISLAYER,
                mm.ArcgisLayer.optionsFromLayerDescription(pjson, {
                  api: arcgis_api,
                  ...source,
                  ...JSON.parse(JSON.stringify(tclayers[pjson.id])),
                })
              );
            layer.id = theLayer.id;
            layer_loaded_cbf(tclayers[pjson.id]);
          },
          (error) => {
            if (cbf) cbf();
            console.log(error);
          }
        );
      };
      load_layer(layers_to_load.shift(), cbs);
    },
    (error) => {
      if (cbf) cbf();
      console.error(error);
    }
  );
}

function _addLayersFromQGIS(
  viewer: Viewer,
  source: any,
  cbs: (source: any) => void,
  cbf?: () => void
) {
  const api = new mm.WFSQGIS(
    source.url,
    source.project || "",
    source.source_options
  );
  api.readLayerOptionsFromCapabilities(
    (layers) => {
      const layers_from_cababilites = layers.reduce((acc, r) => {
        acc[r.layername] = r;
        return acc;
      }, {});

      let layer_configs = [];
      if (source.layers) {
        if (source.layers.length > 0) {
          for (const key in source.layers) {
            const layerconfig = source.layers[key];
            const layer_capabilities =
              layers_from_cababilites[layerconfig.layername];
            layer_configs.push({
              visible:
                typeof source.visible === "undefined" ? true : source.visible,
              expanded:
                typeof source.expanded === "undefined" ? true : source.expanded,
              ...layer_capabilities,
              ...layerconfig,
            });
          }
        }
      }
      if (layer_configs.length == 0) layer_configs = layers;

      const loaded_layers: any = [];
      const load_layer = (
        layer_options: any,
        all_layers_loaded_cbf: (source: any) => void
      ) => {
        const layer_loaded_cbf = (loaded_layer: any) => {
          loaded_layers.push(loaded_layer);
          const next_layer = layer_configs.shift();
          if (next_layer) {
            load_layer(next_layer, all_layers_loaded_cbf);
          } else {
            source.layers = loaded_layers;
            if (all_layers_loaded_cbf) all_layers_loaded_cbf(source);
          }
        };

        api.readLayerPropertyDefs(
          layer_options.layername,
          (layerpropertydefs) => {
            api.readFormatsFromCapabilities(
              (gmlformat, geojsonformat) => {
                api.gmlformat = gmlformat;
                api.geojsonformat = geojsonformat;
                api.readGeometryTypeFromFirstFeature(
                  layer_options.layername,
                  (geometrytype) => {
                    const layer = viewer
                      .getScene()
                      .getLayerHandler()
                      .addLayer(mm.LAYERTYPE.WFSQGISLAYER, {
                        api: api,
                        propertydef: layerpropertydefs,
                        geometrytype: geometrytype,
                        ...JSON.parse(JSON.stringify(layer_options)),
                      });
                    layer_options.id = layer.id;
                    layer_loaded_cbf(layer_options);
                  },
                  (error) => {
                    if (cbf) cbf();
                    console.warn(error);
                  }
                );
              },
              (error) => {
                if (cbf) cbf();
                console.warn(error);
              }
            );
          },
          (error) => {
            if (cbf) cbf();
            console.warn(error);
          }
        );
      };
      load_layer(layer_configs.shift(), cbs);
    },
    (error) => {
      if (cbf) cbf();
      console.warn(error);
    }
  );
}

function _addLayersFromWFS20(
  viewer: Viewer,
  source: any,
  cbs: (source: any) => void,
  cbf?: () => void
) {
  let fetch_options: any = null;
  let namespaces = "";

  if (source.source_options) {
    if (source.source_options.fetch_options) {
      fetch_options = source.source_options.fetch_options;
    }
    if (source.source_options.namespaces) {
      namespaces = source.source_options.namespaces;
    }
  }

  mm.readWFSGetCapabilities(source.url, "2.0.0", fetch_options).then(
    (layers_cpb) => {
      const loaded_layers: any = [];
      const load_layer = (
        layer_options: any,
        all_layers_loaded_cbf: (source: any) => void
      ) => {
        const layer_loaded_cbf = (loaded_layer: any) => {
          if (loaded_layer) loaded_layers.push(loaded_layer);
          const next_layer = source.layers.shift();
          if (next_layer) {
            load_layer(next_layer, all_layers_loaded_cbf);
          } else {
            source.layers = loaded_layers;
            if (all_layers_loaded_cbf) all_layers_loaded_cbf(source);
          }
        };

        const layers_cpb_filtered = layers_cpb.filter((layer_cpb) => {
          return layer_cpb.layername == layer_options.layername;
        });

        if (layers_cpb_filtered.length > 0) {
          const layer_cpb = layers_cpb_filtered[0];
          const wfs_version =
            layer_cpb.version || layer_options.version || "2.0.0";
          const wfs_format =
            layer_cpb.format ||
            layer_options.format ||
            "application/gml+xml; version=3.2";
          mm.readWFS20LayerOptions(
            source.url,
            layer_options.layername,
            wfs_format,
            wfs_version,
            fetch_options
          )
            .then((layer_options_from_server) => {
              const layer = viewer
                .getScene()
                .getLayerHandler()
                .addLayer(mm.LAYERTYPE.WFS20, {
                  url: source.url,
                  fetch_options: fetch_options,
                  namespaces: namespaces,
                  ...layer_cpb,
                  ...layer_options_from_server,
                  ...JSON.parse(JSON.stringify(layer_options)),
                });
              layer_options.id = layer.id;
              layer_loaded_cbf(layer_options);
            })
            .catch((error) => {
              if (cbf) cbf();
              console.warn(error.message);
            });
        } else {
          if (cbf) cbf();
        }
      };
      load_layer(source.layers.shift(), cbs);
    }
  );
}

function _addLayersFromWFS(
  viewer: Viewer,
  source: any,
  cbs: (source: any) => void,
  cbf?: () => void
) {
  if (!source.source_options || !source.source_options?.featureNS) {
    source.source_options = source.source_options || {};
    source.source_options.usePost = false;
    source.editable = false;
  }

  const geoserver_api = new mm.WFSGeoserver(
    source.url,
    source.project,
    source.source_options
  );
  geoserver_api.describeFeatureTypes(
    (json) => {
      const layers_dft = json.featureTypes;
      const layers_gdr = source.layers;

      const layers_dft_map: any = {};
      layers_dft.map((layer_dft: any) => {
        layers_dft_map[layer_dft.typeName] = layer_dft;
      });

      if (layers_gdr) {
        if (layers_gdr.length == 0)
          layers_dft.map((layer_dft: any) => {
            layers_gdr.push({ layername: layer_dft.typeName });
          });
      } else {
        if (cbf) cbf();
      }

      const loaded_layers: any = [];
      const load_layer = (
        layer_gdr: any,
        all_layers_loaded_cbf: (source: any) => void
      ) => {
        const layer_loaded_cbf = (loaded_layer: any) => {
          loaded_layers.push(loaded_layer);
          const next_layer = source.layers.shift();
          if (next_layer) {
            load_layer(next_layer, all_layers_loaded_cbf);
          } else {
            source.layers = loaded_layers;
            if (all_layers_loaded_cbf) all_layers_loaded_cbf(source);
          }
        };

        //dft = describeFeatureType
        const dft = layers_dft_map[layer_gdr.layername];
        geoserver_api.getStyle(
          dft.typeName,
          (style) => {
            const options: any = {
              api: geoserver_api,
            };
            if (style) {
              const styles = mm.WFSGeoserverLayer.parseStyle(style);
              options.style = styles.layerstyle;
              options.highlightstyle = styles.highlightstyle;
            }
            const layer = viewer
              .getScene()
              .getLayerHandler()
              .addLayer(
                mm.LAYERTYPE.WFSLAYER,
                mm.WFSGeoserverLayer.optionsFromDescribeFeatureType(dft, {
                  ...options,
                  ...source,
                  ...JSON.parse(JSON.stringify(layer_gdr)),
                })
              );
            layer_gdr.id = layer.id;
            layer_loaded_cbf(layer_gdr);
          },
          (error) => {
            if (cbf) cbf();
            console.warn(error);
          }
        );
      };
      load_layer(layers_gdr.shift(), cbs);
      return {};
    },
    (error) => {
      if (cbf) cbf();
      console.warn(error);
      return {};
    }
  );
}
